import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { FormModal } from '@/components/ui/form-modal'
import {
  NumberFieldControl,
  SelectFieldControl,
  TextFieldControl,
} from '@/components/ui/form-fields'

import type { CreateZoneBody, PosBranchRef, PosZone, UpdateZoneBody } from '../types'
import { branchLabel } from './pos-format'

const schema = z.object({
  branchId: z.string().min(1, 'Choose an outlet.'),
  name: z.string().trim().min(1, 'Give the zone a name.').max(80),
  sortOrder: z.number().int().min(0).max(9999).optional(),
})

type ZoneFormValues = z.infer<typeof schema>

interface ZoneFormDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /** Null creates. */
  zone: PosZone | null
  branches: PosBranchRef[]
  isSubmitting: boolean
  error?: unknown
  onCreate: (body: CreateZoneBody) => Promise<unknown>
  onUpdate: (id: string, body: UpdateZoneBody) => Promise<unknown>
}

/**
 * Add or rename a room.
 *
 * The outlet is fixed once the zone exists. A zone that changed branch would
 * leave its tables behind at the old one — "Main Hall" at two outlets is two
 * rooms that happen to share a name, not one room in two places.
 */
export function ZoneFormDialog({
  isOpen,
  onOpenChange,
  zone,
  branches,
  isSubmitting,
  error,
  onCreate,
  onUpdate,
}: ZoneFormDialogProps) {
  const isEditing = Boolean(zone)

  const { control, handleSubmit } = useForm<ZoneFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      branchId: zone?.branchId ?? branches[0]?.id ?? '',
      name: zone?.name ?? '',
      sortOrder: zone?.sortOrder ?? 0,
    },
  })

  async function submit(values: ZoneFormValues) {
    if (zone) {
      await onUpdate(zone.id, { name: values.name.trim(), sortOrder: values.sortOrder })
    } else {
      await onCreate({
        branchId: values.branchId,
        name: values.name.trim(),
        sortOrder: values.sortOrder,
      })
    }

    onOpenChange(false)
  }

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={isEditing ? `Rename ${zone?.name}` : 'Add a zone'}
      description="A zone groups tables inside one outlet — Main Hall, Bar, Patio."
      submitLabel={isEditing ? 'Save zone' : 'Add zone'}
      onSubmit={handleSubmit(submit)}
      isBusy={isSubmitting}
      error={error}
      size="sm"
    >
      <SelectFieldControl
        control={control}
        name="branchId"
        label="Outlet"
        isRequired
        isDisabled={isEditing}
        options={branches.map((branch) => ({
          value: branch.id,
          label: branchLabel(branch),
        }))}
      />

      <TextFieldControl
        control={control}
        name="name"
        label="Name"
        placeholder="Main Hall"
        isRequired
      />

      <NumberFieldControl
        control={control}
        name="sortOrder"
        label="Order"
        minValue={0}
        maxValue={9999}
        step={1}
        description="Where it sits on the floor grid. Zones with the same number fall back to name order."
      />
    </FormModal>
  )
}
